import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  getRetryDelay,
  reportQueryError,
  shouldRetry,
} from './Query.helpers.ts';
import type {
  TQueryErrorHandlers,
  TQueryProviderProps,
} from './Query.types.ts';
import { useLatest } from '#hooks/use-latest/index.ts';

const DEFAULT_STALE_TIME = 300_000;
const DEFAULT_GC_TIME = 300_000;

const createClient = (getHandlers: () => TQueryErrorHandlers): QueryClient =>
  new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) =>
        reportQueryError(
          error,
          { source: 'query', queryKey: query.queryKey },
          getHandlers(),
        ),
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _onMutateResult, mutation) =>
        reportQueryError(
          error,
          { source: 'mutation', mutationKey: mutation.options.mutationKey },
          getHandlers(),
        ),
    }),
    defaultOptions: {
      queries: {
        staleTime: DEFAULT_STALE_TIME,
        gcTime: DEFAULT_GC_TIME,
        retry: shouldRetry,
        retryDelay: getRetryDelay,
      },
    },
  });

export const QueryProvider = ({
  reporter,
  onUnauthenticated,
  children,
}: TQueryProviderProps) => {
  const handlers = useLatest({ reporter, onUnauthenticated });
  const [client] = useState(() => createClient(() => handlers.current));

  useEffect(() => () => client.clear(), [client]);

  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
};
